import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { CardWithHeader } from "@/components/card-with-header";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { VolumeApi } from "@/lib/volume-api";
import { getMessageFromError } from "@/lib/api-errors";
import { numberToDecimalWithCurrency } from "@/lib/utils";
import { AgentPaymentConfig } from "@/types/agent";

export const Route = createFileRoute(
  "/(app)/_main-layout/_in-store/in-store/agents_/$agentId/config",
)({
  component: AgentConfigPage,
});

function AgentConfigPage() {
  const context = Route.useRouteContext();
  const { agentId } = Route.useParams();

  const configQuery = useQuery({
    queryKey: ["agent-payment-config", agentId, context.selectedApplicationId],
    queryFn: async () => {
      const api = new VolumeApi(
        context.accessToken,
        context.selectedApplicationId,
      );
      try {
        return await api.getAgentPaymentConfig({ agentId });
      } catch (err) {
        if ((err as { status?: number })?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
  });

  const historyQuery = useQuery({
    queryKey: [
      "agent-payment-config-history",
      agentId,
      context.selectedApplicationId,
    ],
    queryFn: () => {
      const api = new VolumeApi(
        context.accessToken,
        context.selectedApplicationId,
      );
      return api.getAgentPaymentConfigHistory({ agentId });
    },
    retry: false,
  });

  return (
    <div className="space-y-6">
      <CardWithHeader
        variant="exchange"
        label="Payment configuration"
        title="Current configuration"
      >
        {configQuery.isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Spinner />
          </div>
        ) : configQuery.isError ? (
          <div className="py-4 text-sm text-red-600">
            Failed to load payment configuration:{" "}
            {getMessageFromError(configQuery.error)}
          </div>
        ) : configQuery.data ? (
          <CurrentConfig config={configQuery.data} />
        ) : (
          <div className="py-4 text-sm text-slate-500">
            No active payment configuration found.
          </div>
        )}
      </CardWithHeader>

      <CardWithHeader
        variant="exchange"
        label="History"
        title="Configuration history"
      >
        {historyQuery.isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Spinner />
          </div>
        ) : historyQuery.isError ? (
          <div className="py-4 text-sm text-red-600">
            Failed to load history: {getMessageFromError(historyQuery.error)}
          </div>
        ) : !historyQuery.data || historyQuery.data.length === 0 ? (
          <div className="py-4 text-sm text-slate-500">
            No historical configurations to show.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Amount</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Last updated</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {historyQuery.data.map((entry) => (
                <TableRow key={`${entry.version}-${entry.lastUpdatedAt}`}>
                  <TableCell>
                    {numberToDecimalWithCurrency(entry.amount, entry.currency)}{" "}
                    <span className="text-xs text-slate-500">
                      {entry.currency}
                    </span>
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {entry.reference ?? "—"}
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {entry.description ?? "—"}
                  </TableCell>
                  <TableCell>
                    {entry.active ? (
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                        Active
                      </span>
                    ) : (
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                        Inactive
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {new Date(entry.lastUpdatedAt).toLocaleString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardWithHeader>

      <p className="text-xs text-slate-500">
        Configuration changes require a signed request and must be made from a
        secure backend environment — see the{" "}
        <a
          href="https://docs.getvolume.com/API/agent/agent-api-docs.html"
          target="_blank"
          rel="noreferrer"
          className="text-volume-text-purple hover:underline"
        >
          Agent API docs
        </a>
        .
      </p>
    </div>
  );
}

function CurrentConfig({ config }: { config: AgentPaymentConfig }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
      <Field
        label="Amount"
        value={`${numberToDecimalWithCurrency(config.amount, config.currency)} ${config.currency}`}
      />
      <Field
        label="Status"
        value={
          config.active ? (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
              Active
            </span>
          ) : (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
              Inactive
            </span>
          )
        }
      />
      <Field label="Reference" value={config.reference ?? "—"} />
      <Field label="Description" value={config.description ?? "—"} />
      <Field
        label="Last updated"
        value={new Date(config.lastUpdatedAt).toLocaleString()}
      />
    </dl>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-slate-800">{value}</dd>
    </div>
  );
}
